import React, { useState, useMemo, useCallback } from 'react'
import {
  Grid, Card, CardContent, Typography, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Paper,
  TextField, FormControl, InputLabel, Select, MenuItem,
  IconButton, Tooltip, TablePagination, Box, Chip,
  Dialog, DialogTitle, DialogContent, DialogActions, Button
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { Edit, Business, AttachMoney, Search, Block, Warning, Store, Assessment } from '@material-ui/icons'
import CountUp from 'react-countup'
import stockService from '../../services/StockService'
import { useSelector } from 'react-redux'

const useStyles = makeStyles((theme) => ({
  statCard: {
    borderLeft: `4px solid ${theme.palette.primary.main}`,
    padding: theme.spacing(2),
    height: '100%',
    transition: 'transform 0.2s',
    '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: theme.shadows[4]
    }
  },
  statCardWarning: {
    borderLeft: `4px solid #FF9800`,
    padding: theme.spacing(2),
    height: '100%',
    transition: 'transform 0.2s',
    '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: theme.shadows[4]
    }
  },
  statCardDanger: {
    borderLeft: `4px solid #f44336`,
    padding: theme.spacing(2),
    height: '100%',
    transition: 'transform 0.2s',
    '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: theme.shadows[4]
    }
  },
  statCardSuccess: {
    borderLeft: `4px solid #4CAF50`,
    padding: theme.spacing(2),
    height: '100%',
    transition: 'transform 0.2s',
    '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: theme.shadows[4]
    }
  },
  statCardPrimary: {
    borderLeft: `4px solid #2196F3`,
    padding: theme.spacing(2),
    height: '100%',
    transition: 'transform 0.2s',
    '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: theme.shadows[4]
    }
  },
  tableHeader: {
    backgroundColor: theme.palette.primary.main,
    '& th': {
      color: 'white',
      fontWeight: 'bold',
      fontSize: '14px'
    }
  },
  lowStock: {
    backgroundColor: '#fff3e0 !important',
    '&:hover': {
      backgroundColor: '#ffecb3 !important'
    }
  },
  expired: {
    backgroundColor: '#ffebee !important',
    '&:hover': {
      backgroundColor: '#ffcdd2 !important'
    }
  },
  normal: {
    backgroundColor: '#e8f5e8 !important',
    '&:hover': {
      backgroundColor: '#c8e6c9 !important'
    }
  },
  critical: {
    backgroundColor: '#ffcdd2 !important',
    '&:hover': {
      backgroundColor: '#ef9a9a !important'
    }
  },
  statusBadge: {
    padding: '4px 8px',
    borderRadius: '12px',
    fontSize: '0.75rem',
    fontWeight: 'bold',
    minWidth: '80px',
    textAlign: 'center',
    display: 'inline-block'
  },
  statusNormal: {
    backgroundColor: '#4CAF50',
    color: 'white'
  },
  statusLow: {
    backgroundColor: '#FF9800',
    color: 'white'
  },
  statusExpired: {
    backgroundColor: '#f44336',
    color: 'white'
  },
  statusCritical: {
    backgroundColor: '#d32f2f',
    color: 'white'
  },
  positive: {
    color: '#4CAF50',
    fontWeight: 'bold'
  },
  statIcon: {
    fontSize: '40px',
    marginBottom: '10px',
    opacity: 0.8
  },
  totalValue: {
    fontSize: '1.5rem',
    fontWeight: 'bold',
    color: '#2E7D32'
  },
  totalGlobalValue: {
    fontSize: '1.5rem',
    fontWeight: 'bold',
    color: '#2196F3'
  },
  searchContainer: {
    padding: theme.spacing(2),
    backgroundColor: '#f5f5f5',
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`
  },
  searchField: {
    backgroundColor: 'white',
    borderRadius: theme.shape.borderRadius,
    '& .MuiOutlinedInput-root': {
      '&:hover fieldset': {
        borderColor: theme.palette.primary.main,
      },
    }
  },
  filterSection: {
    display: 'flex',
    gap: theme.spacing(2),
    alignItems: 'center',
    flexWrap: 'wrap'
  },
  quantiteInput: {
    width: '80px',
    '& input': {
      textAlign: 'center',
      fontWeight: 'bold'
    },
    '& .MuiOutlinedInput-root': {
      '&:hover fieldset': {
        borderColor: '#4CAF50',
      },
    }
  },
  totalRow: {
    backgroundColor: '#f5f5f5 !important',
    '& td': {
      fontWeight: 'bold',
      fontSize: '14px'
    }
  },
  montantCell: {
    fontWeight: 'bold',
    fontSize: '14px',
    color: '#2E7D32'
  }
}))

const StockActuel = ({ stockData = [], loading, onRefresh, onSnackbar }) => {
  const classes = useStyles()
  const auth = useSelector((state) => state.auth)
  
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('TOUS')
  const [pagination, setPagination] = useState({
    page: 0,
    rowsPerPage: 10
  })
  const [selectedProduct, setSelectedProduct] = useState(null)
  const [defectDialogOpen, setDefectDialogOpen] = useState(false)
  const [quantiteModifiee, setQuantiteModifiee] = useState({})
  const [formData, setFormData] = useState({
    quantite_avarie: '',
    motif: '',
    etat_produit: 'AVARIE'
  })

  // Vérifier si l'utilisateur est admin
  const isAdmin = auth?.rol === "admin"

  // Sécuriser les données
  const safeStockData = useMemo(() => 
    Array.isArray(stockData) ? stockData : [],
    [stockData]
  )

  // Calcul des statistiques en temps réel
  const stats = useMemo(() => {
    if (!safeStockData || safeStockData.length === 0) {
      return {
        totalValue: 0,
        lowStockCount: 0,
        expiredCount: 0,
        criticalStockCount: 0,
        normalStockCount: 0,
        totalQuantite: 0,
        montantTotalGlobal: 0
      }
    }

    return safeStockData.reduce((acc, produit) => {
      const quantite = parseInt(produit.quantite_stock || 0)
      // Utiliser prix_unitaire s'il existe, sinon prixProd
      const prix = parseFloat(produit.prix_unitaire || produit.prixProd || 0)
      const productValue = quantite * prix
      
      // Montant total global
      acc.montantTotalGlobal += productValue
      
      // Valeur totale (calculée de la même manière pour compatibilité)
      acc.totalValue += productValue
      
      // Quantité totale en stock
      acc.totalQuantite += quantite

      // Comptage par statut
      if (produit.statut_produit === 'EXPIRER') {
        acc.expiredCount++
      } else if (quantite <= 2) {
        acc.criticalStockCount++
      } else if (quantite <= 5) {
        acc.lowStockCount++
      } else {
        acc.normalStockCount++
      }

      return acc
    }, {
      totalValue: 0,
      lowStockCount: 0,
      expiredCount: 0,
      criticalStockCount: 0,
      normalStockCount: 0,
      totalQuantite: 0,
      montantTotalGlobal: 0
    })
  }, [safeStockData])

  // Filtrage des produits
  const filteredStockData = useMemo(() => {
    if (!safeStockData || safeStockData.length === 0) return []
    
    return safeStockData.filter(produit => {
      const matchesSearch = searchTerm === '' || 
        produit.nomProd?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        produit.codeBarre?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        produit.intituleFam?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (produit.montant_total_produit?.toString() || '').includes(searchTerm)

      const matchesStatus = statusFilter === 'TOUS' || getStatusText(produit) === statusFilter

      return matchesSearch && matchesStatus
    })
  }, [safeStockData, searchTerm, statusFilter])

  // Calcul du montant total des produits filtrés
  const montantTotalFiltre = useMemo(() => {
    if (!filteredStockData || filteredStockData.length === 0) return 0
    
    return filteredStockData.reduce((total, produit) => {
      const quantite = parseInt(produit.quantite_stock || 0)
      const prix = parseFloat(produit.prix_unitaire || produit.prixProd || 0)
      return total + (quantite * prix)
    }, 0)
  }, [filteredStockData])

  // Données paginées
  const paginatedData = useMemo(() => 
    filteredStockData.slice(
      pagination.page * pagination.rowsPerPage,
      (pagination.page + 1) * pagination.rowsPerPage
    ),
    [filteredStockData, pagination.page, pagination.rowsPerPage]
  )

  // Callbacks
  const handleSearchChange = useCallback((event) => {
    setSearchTerm(event.target.value)
    setPagination(prev => ({ ...prev, page: 0 }))
  }, [])

  const handleStatusFilterChange = useCallback((event) => {
    setStatusFilter(event.target.value)
    setPagination(prev => ({ ...prev, page: 0 }))
  }, [])

  const handlePageChange = useCallback((event, newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }))
  }, [])

  const handleRowsPerPageChange = useCallback((event) => {
    setPagination({
      page: 0,
      rowsPerPage: parseInt(event.target.value, 10)
    })
  }, [])

  // Modification directe de la quantité
  const handleQuantiteChange = useCallback((produitId, nouvelleQuantite) => {
    setQuantiteModifiee(prev => ({
      ...prev,
      [produitId]: parseInt(nouvelleQuantite) || 0
    }))
  }, [])

  // Sauvegarde rapide de la quantité modifiée
  const handleSauvegarderQuantite = useCallback(async (produit) => {
    try {
      const nouvelleQuantite = quantiteModifiee[produit.codeIdProd]
      
      if (nouvelleQuantite === undefined) {
        onSnackbar('Aucune modification détectée')
        return
      }

      if (nouvelleQuantite < 0) {
        onSnackbar('La quantité ne peut pas être négative')
        return
      }

      await stockService.ajusterStock({
        codeIdProd: produit.codeIdProd,
        nouvelleQuantite: nouvelleQuantite,
        motif: 'Ajustement direct depuis l\'interface',
        speudo: auth?.username || 'utilisateur'
      })

      // Nettoyer la modification
      setQuantiteModifiee(prev => {
        const newState = { ...prev }
        delete newState[produit.codeIdProd]
        return newState
      })

      onRefresh()
      onSnackbar('Quantité mise à jour avec succès')
    } catch (error) {
      console.error('Erreur mise à jour quantité:', error)
      onSnackbar('Erreur lors de la mise à jour de la quantité')
    }
  }, [quantiteModifiee, auth, onSnackbar, onRefresh])

  const handleCreateDefect = useCallback(async () => {
    try {
      const quantiteAvarie = parseInt(formData.quantite_avarie) || 0
      const stockActuel = parseInt(selectedProduct?.quantite_stock || 0)
      
      if (quantiteAvarie <= 0) {
        onSnackbar('La quantité doit être supérieure à 0')
        return
      }

      if (quantiteAvarie > stockActuel) {
        onSnackbar(`La quantité ne peut pas dépasser le stock actuel (${stockActuel})`)
        return
      }

      await stockService.createDefect({
        quan_avarie: quantiteAvarie, 
        codeIdProd: selectedProduct?.codeIdProd,
        etat_produit: formData.etat_produit,
        motif: formData.motif,
        speudo: auth?.username || 'utilisateur'
      })
      
      setDefectDialogOpen(false)
      setFormData({ quantite_avarie: '', motif: '', etat_produit: 'AVARIE' })
      onRefresh()
      onSnackbar('Défaut enregistré avec succès')
    } catch (error) {
      console.error('Erreur création défaut:', error)
      onSnackbar('Erreur lors de la création du défaut')
    }
  }, [formData, selectedProduct, auth, onRefresh, onSnackbar])

  // Fonctions utilitaires
  const getStatusClass = useCallback((produit) => {
    const quantite = parseInt(produit.quantite_stock || 0)
    if (produit.statut_produit === 'EXPIRER') return classes.statusExpired
    if (quantite <= 2) return classes.statusCritical
    if (quantite <= 5) return classes.statusLow
    return classes.statusNormal
  }, [classes])

  const getStatusText = useCallback((produit) => {
    const quantite = parseInt(produit.quantite_stock || 0)
    if (produit.statut_produit === 'EXPIRER') return 'EXPIRÉ'
    if (quantite <= 2) return 'CRITIQUE'
    if (quantite <= 5) return 'FAIBLE'
    return 'NORMAL'
  }, [])

  const getRowClass = useCallback((produit) => {
    const quantite = parseInt(produit.quantite_stock || 0)
    if (produit.statut_produit === 'EXPIRER') return classes.expired
    if (quantite <= 2) return classes.critical
    if (quantite <= 5) return classes.lowStock
    return classes.normal
  }, [classes])

  const getStockLevelColor = useCallback((quantite) => {
    const qte = parseInt(quantite || 0)
    if (qte <= 2) return '#f44336'
    if (qte <= 5) return '#FF9800'
    return '#4CAF50'
  }, [])

  // Composant mémoisé pour l'affichage des montants
  const FormattedMontant = useMemo(() => ({ montant, className = '' }) => {
    return (
      <CountUp 
        start={0} 
        end={montant || 0} 
        duration={1.5} 
        separator=" " 
        decimals={0}
        suffix=" F CFA"
        className={className}
      />
    )
  }, [])

  // Fonction pour formater les grands nombres
  const formatMontant = useCallback((montant) => {
    return new Intl.NumberFormat('fr-FR').format(Math.round(montant))
  }, [])

  return (
    <Grid container spacing={3}>
      {/* Cartes de statistiques rapides */}
      <Grid item xs={12} md={3}>
        <Card className={classes.statCard}>
          <CardContent>
            <Store className={classes.statIcon} style={{ color: '#2196F3' }} />
            <Typography variant="h6" color="textSecondary">
              Produits en stock
            </Typography>
            <Typography variant="h4">
              {safeStockData.length.toLocaleString()}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              {stats.totalQuantite.toLocaleString()} unités
            </Typography>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} md={3}>
        <Card className={classes.statCardSuccess}>
          <CardContent>
            <AttachMoney className={classes.statIcon} style={{ color: '#4CAF50' }} />
            <Typography variant="h6" color="textSecondary">
              Valeur totale stock
            </Typography>
            <Typography variant="h4" className={classes.totalValue}>
              <FormattedMontant montant={stats.montantTotalGlobal} />
            </Typography>
            <Typography variant="caption" color="textSecondary">
              {formatMontant(stats.montantTotalGlobal)} F CFA
            </Typography>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} md={3}>
        <Card className={classes.statCardPrimary}>
          <CardContent>
            <Assessment className={classes.statIcon} style={{ color: '#2196F3' }} />
            <Typography variant="h6" color="textSecondary">
              Valeur filtrée
            </Typography>
            <Typography variant="h4" className={classes.totalGlobalValue}>
              <FormattedMontant montant={montantTotalFiltre} />
            </Typography>
            <Typography variant="caption" color="textSecondary">
              {filteredStockData.length} produit(s)
            </Typography>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} md={3}>
        <Card className={classes.statCardWarning}>
          <CardContent>
            <Warning className={classes.statIcon} style={{ color: '#FF9800' }} />
            <Typography variant="h6" color="textSecondary">
              Stock faible/critique
            </Typography>
            <Typography variant="h4" style={{ color: '#FF9800' }}>
              {(stats.lowStockCount + stats.criticalStockCount).toLocaleString()}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {stats.criticalStockCount} critique(s)
            </Typography>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} md={3}>
        <Card className={classes.statCardDanger}>
          <CardContent>
            <Block className={classes.statIcon} style={{ color: '#f44336' }} />
            <Typography variant="h6" color="textSecondary">
              Produits expirés
            </Typography>
            <Typography variant="h4" style={{ color: '#f44336' }}>
              {stats.expiredCount.toLocaleString()}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              À retirer
            </Typography>
          </CardContent>
        </Card>
      </Grid>

      {/* Barre de recherche et filtres */}
      <Grid item xs={12}>
        <Card className={classes.searchContainer}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              🔍 Rechercher et filtrer les produits
            </Typography>
            <div className={classes.filterSection}>
              <TextField
                className={classes.searchField}
                placeholder="Rechercher par nom, code barre, famille ou montant..."
                value={searchTerm}
                onChange={handleSearchChange}
                InputProps={{
                  startAdornment: <Search style={{ marginRight: 8, color: '#757575' }} />,
                }}
                style={{ minWidth: 300 }}
                variant="outlined"
                size="small"
              />
              
              <FormControl variant="outlined" size="small" style={{ minWidth: 150 }}>
                <InputLabel>Statut</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={handleStatusFilterChange}
                  label="Statut"
                >
                  <MenuItem value="TOUS">Tous les statuts</MenuItem>
                  <MenuItem value="NORMAL">Normal</MenuItem>
                  <MenuItem value="FAIBLE">Faible</MenuItem>
                  <MenuItem value="CRITIQUE">Critique</MenuItem>
                  <MenuItem value="EXPIRÉ">Expiré</MenuItem>
                </Select>
              </FormControl>

              <Box style={{ marginLeft: 'auto', textAlign: 'right' }}>
                <Typography variant="body2" color="textSecondary">
                  {filteredStockData.length} produit(s) trouvé(s)
                </Typography>
                <Typography variant="body2" color="primary" style={{ fontWeight: 'bold' }}>
                  Total filtré: {formatMontant(montantTotalFiltre)} F CFA
                </Typography>
              </Box>
            </div>
          </CardContent>
        </Card>
      </Grid>

      {/* Tableau des produits */}
      <Grid item xs={12}>
        <Card>
          <CardContent style={{ padding: 0 }}>
            <Box p={2} display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="h6" gutterBottom>
                Liste des produits en stock ({filteredStockData.length} produit(s) filtré(s))
              </Typography>
              <Typography variant="subtitle2" color="primary" style={{ fontWeight: 'bold' }}>
                Valeur totale: {formatMontant(montantTotalFiltre)} F CFA
              </Typography>
            </Box>
            <TableContainer component={Paper}>
              <Table>
                <TableHead className={classes.tableHeader}>
                  <TableRow>
                    <TableCell>Produit</TableCell>
                    <TableCell>Code Barre</TableCell>
                    <TableCell align="center">Quantité en Stock</TableCell>
                    <TableCell align="right">Prix Unitaire</TableCell>
                    <TableCell align="right">Valeur Stock</TableCell>
                    <TableCell>Statut</TableCell>
                    {isAdmin && (
                      <TableCell align="center">Actions</TableCell>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedData.map((produit) => {
                    const quantiteActuelle = parseInt(produit.quantite_stock || 0)
                    const quantiteModif = quantiteModifiee[produit.codeIdProd]
                    const quantiteAffichage = quantiteModif !== undefined ? quantiteModif : quantiteActuelle
                    // Utiliser prix_unitaire s'il existe, sinon prixProd
                    const prix = parseFloat(produit.prix_unitaire || produit.prixProd || 0)
                    const valeurStock = quantiteAffichage * prix
                    const estModifie = quantiteModif !== undefined
                    
                    return (
                      <TableRow key={produit.codeIdProd} className={getRowClass(produit)}>
                        <TableCell>
                          <Typography variant="subtitle2" style={{ fontWeight: 'bold' }}>
                            {produit.nomProd}
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            {produit.FormFarmaceutik} • {produit.intituleFam}
                          </Typography>
                          {produit.nomFournisseur && (
                            <Typography variant="caption" color="textSecondary" display="block">
                              Fournisseur: {produit.nomFournisseur}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={produit.codeBarre || 'N/A'} 
                            size="small" 
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
                            <TextField
                              className={classes.quantiteInput}
                              type="number"
                              value={quantiteAffichage}
                              onChange={(e) => handleQuantiteChange(produit.codeIdProd, e.target.value)}
                              InputProps={{
                                inputProps: { 
                                  min: 0,
                                  style: { 
                                    color: getStockLevelColor(quantiteAffichage),
                                    fontWeight: 'bold'
                                  }
                                }
                              }}
                              variant="outlined"
                              size="small"
                            />
                            {estModifie && isAdmin && (
                              <Tooltip title="Sauvegarder la modification">
                                <IconButton 
                                  size="small" 
                                  onClick={() => handleSauvegarderQuantite(produit)}
                                  style={{ color: '#4CAF50' }}
                                >
                                  <Edit />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                          {estModifie && (
                            <Typography variant="caption" color="textSecondary">
                              Ancienne: {quantiteActuelle}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="right">
                          <Typography className={classes.positive}>
                            {formatMontant(prix)} F CFA
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography className={classes.montantCell}>
                            {formatMontant(valeurStock)} F CFA
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={getStatusText(produit)}
                            className={`${classes.statusBadge} ${getStatusClass(produit)}`}
                            size="small"
                          />
                        </TableCell>
                        {isAdmin && (
                          <TableCell align="center">
                            <Tooltip title="Signaler un défaut">
                              <IconButton 
                                size="small" 
                                onClick={() => {
                                  setSelectedProduct(produit)
                                  setDefectDialogOpen(true)
                                }}
                                style={{ color: '#FF9800' }}
                              >
                                <Warning />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        )}
                      </TableRow>
                    )
                  })}
                  
                  {/* Ligne de total pour la page actuelle */}
                  {paginatedData.length > 0 && (
                    <TableRow className={classes.totalRow}>
                      <TableCell colSpan={isAdmin ? 5 : 4} align="right">
                        <Typography variant="subtitle2">
                          Total de la page ({paginatedData.length} produits):
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="subtitle2" className={classes.montantCell}>
                          {formatMontant(
                            paginatedData.reduce((total, produit) => {
                              const quantite = parseInt(produit.quantite_stock || 0)
                              const prix = parseFloat(produit.prix_unitaire || produit.prixProd || 0)
                              return total + (quantite * prix)
                            }, 0)
                          )} F CFA
                        </Typography>
                      </TableCell>
                      {isAdmin && <TableCell></TableCell>}
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            
            {/* Pagination */}
            <TablePagination
              rowsPerPageOptions={[10, 25, 50, 100]}
              component="div"
              count={filteredStockData.length}
              rowsPerPage={pagination.rowsPerPage}
              page={pagination.page}
              onPageChange={handlePageChange}
              onRowsPerPageChange={handleRowsPerPageChange}
              labelRowsPerPage="Lignes par page:"
              labelDisplayedRows={({ from, to, count }) => 
                `${from}-${to} sur ${count !== -1 ? count : `plus de ${to}`}`
              }
            />
          </CardContent>
        </Card>
      </Grid>

      {/* Dialog pour signaler un défaut */}
      <Dialog open={defectDialogOpen} onClose={() => setDefectDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Signaler un défaut/avarie</DialogTitle>
        <DialogContent>
          <Typography variant="subtitle1" gutterBottom style={{ fontWeight: 'bold' }}>
            Produit: {selectedProduct?.nomProd}
          </Typography>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            Stock actuel: {parseInt(selectedProduct?.quantite_stock || 0)} unités
          </Typography>
          <TextField
            fullWidth
            label="Quantité défectueuse"
            type="number"
            value={formData.quantite_avarie}
            onChange={(e) => setFormData({...formData, quantite_avarie: e.target.value})}
            margin="normal"
            InputProps={{ 
              inputProps: { 
                min: 1, 
                max: parseInt(selectedProduct?.quantite_stock || 0) 
              }
            }}
            helperText={`Maximum: ${parseInt(selectedProduct?.quantite_stock || 0)} unités`}
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>Type de défaut</InputLabel>
            <Select
              value={formData.etat_produit}
              onChange={(e) => setFormData({...formData, etat_produit: e.target.value})}
            >
              <MenuItem value="AVARIE">Avarie</MenuItem>
              <MenuItem value="EXPIRER">Produit expiré</MenuItem>
              <MenuItem value="RETRAIT">Retrait</MenuItem>
            </Select>
          </FormControl>
          <TextField
            fullWidth
            label="Motif"
            multiline
            rows={3}
            value={formData.motif}
            onChange={(e) => setFormData({...formData, motif: e.target.value})}
            margin="normal"
            placeholder="Décrivez la raison du défaut..."
            helperText="Obligatoire"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDefectDialogOpen(false)}>Annuler</Button>
          <Button 
            onClick={handleCreateDefect} 
            color="primary" 
            variant="contained"
            disabled={!formData.motif.trim() || !formData.quantite_avarie}
          >
            Enregistrer
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  )
}

export default React.memo(StockActuel)